export const ROLES = {
  ADMIN: 1,
  USER: 2,
  CMS_ADMIN: 3,
} as const;

export const SHOP_URL = 'https://endoaxis.store/';

export const ACCESS_TOKEN_KEY = 'ea_access_token';
export const REFRESH_TOKEN_KEY = 'ea_refresh_token';

export const ROUTES = {
  // Public marketing pages
  LANDING: '/',
  ABOUT: '/about',
  PRODUCTS: '/products',
  TECHNOLOGY: '/technology',
  FAQ: '/faq',
  CONTACT: '/contact',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  // Partner integration landing pages. The Evexia path is embedded in their partner tile,
  // so changing it means a change request to Evexia - see ENDO-1295.
  INTEGRATIONS_EVEXIA: '/integrations/evexia',
  // Public content pages (Phase 3, add routes now)
  BLOG: '/blog',
  BLOG_POST: '/blog/:slug',
  HELP_CENTER: '/help',
  HELP_ARTICLE: '/help/:categorySlug/:slug',
  HELP_CATEGORY_VIEW: '/help/:categorySlug',
  HELP_CATEGORY_ARTICLE: '/help/:categorySlug/:slug',
  RESOURCES: '/resources',
  RESOURCE: '/resources/:slug',
  RESOURCE_CATEGORY: '/resources/:categorySlug',
  RESOURCE_ARTICLE: '/resources/:categorySlug/:slug',
  EVENTS: '/events',
  EVENT: '/events/:slug',
  // Auth pages
  LOGIN: '/login',
  SIGNUP: '/signup',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  EMAIL_CONFIRMATION: '/email-confirmation',
  DASHBOARD: '/dashboard',
  PATIENTS: '/patients',
  PATIENT_NEW: '/patients/new',
  PATIENT_NEW_UPLOAD: '/patients/new/upload',
  PATIENT_NEW_ACTION: '/patients/new/action',
  PATIENT_NEW_AXISMATRIX: '/patients/new/axis-matrix',
  PATIENT_NEW_CONFIRM: '/patients/new/confirm',
  PATIENT_NEW_SYMPTOMS: '/patients/new/symptoms',
  PATIENT_PROFILE: '/patients/:patientId',
  /** Provider entering an assigned AxisMatrix quiz on the patient's behalf. */
  PATIENT_AXISMATRIX_TAKE: '/patients/:patientId/axis-matrix/:hash',
  PATIENT_REVIEW: '/patients/:patientId/review/:labResultId',
  PATIENT_RESULTS: '/patients/:patientId/results/:labResultId',
  REPORTS_HORMONE: '/patients/:patientId/results/:labResultId/report/hormone',
  REPORTS_FORMULATIONS: '/patients/:patientId/results/:labResultId/report/formulations',
  REPORTS_LIFESTYLE: '/patients/:patientId/results/:labResultId/report/lifestyle',
  SHOPIFY_CART: '/shopify-cart',
  ADMIN_DASHBOARD: '/admin',
  ADMIN_PATIENTS: '/admin/patients',
  ADMIN_PATIENT_MERGE: '/admin/patients/merge',
  ADMIN_QUEUE: '/admin/queue',
  ADMIN_PRACTITIONERS: '/admin/practitioners',
  ADMIN_PRACTITIONER_DETAIL: '/admin/practitioners/:practitionerId',
  ADMIN_USERS: '/admin/users',
  ADMIN_COMMENTS: '/admin/comments',
  ADMIN_SYSTEM: '/admin/system',
  ADMIN_2FA: '/admin/2fa',
  ADMIN_HORMONES: '/admin/hormones',
  ADMIN_TRIGGERS: '/admin/triggers',
  ADMIN_HORMONE_LEVELS: '/admin/hormone-levels',
  ADMIN_CONTENT: '/admin/content',
  ADMIN_CONTENT_NEW: '/admin/content/new',
  ADMIN_CONTENT_EDIT: '/admin/content/:id/edit',
  ADMIN_CONTENT_CATEGORIES: '/admin/content/categories',
  ADMIN_CONTACTS: '/admin/contacts',
  ADMIN_PRODUCT_CATALOG: '/admin/product-catalog',
  ADMIN_PRODUCT_CATALOG_PRODUCTS: '/admin/product-catalog/products',
  ADMIN_PRODUCT_CATALOG_VENDORS: '/admin/product-catalog/vendors',
  ADMIN_PRODUCT_CATALOG_CATEGORIES: '/admin/product-catalog/categories',
  ADMIN_CONFIG: '/admin/config',
  ADMIN_SESSIONS: '/admin/sessions',
  ADMIN_SESSION_DETAIL: '/admin/sessions/:id',
  ADMIN_SETTINGS: '/admin/settings',
  ADMIN_PROFILE: '/admin/profile',
  ADMIN_FEATURE_FLAGS: '/admin/feature-flags',
  ADMIN_SES_EMAIL_TEMPLATES: '/admin/ses-email-templates',
  ADMIN_FORMULA_BUILDER: '/admin/formula-builder',
  ADMIN_ALGORITHM_MANAGEMENT: '/admin/algorithm-management',
  ADMIN_AXISMATRIX_MANAGEMENT: '/admin/axis-matrix-management',
  ADMIN_TELEMETRY: '/admin/telemetry',
  ADMIN_TELEMETRY_EMAIL: '/admin/telemetry/email',
  ADMIN_TELEMETRY_LOGS: '/admin/telemetry/logs',
  ADMIN_TELEMETRY_TRACES: '/admin/telemetry/traces',
  ADMIN_TELEMETRY_TRACE_DETAIL: '/admin/telemetry/traces/:traceId',
  ADMIN_DEAD_LETTERS: '/admin/telemetry/dead-letters',
  ADMIN_WEBHOOKS: '/admin/telemetry/webhooks',
  ADMIN_HELD_RESULTS: '/admin/telemetry/held-results',
  PROFILE: '/profile',
  ACCOUNT: '/account',
  FULLSCRIPT_CALLBACK: '/fullscript-callback',
  QUIZ: '/quiz',
  SHOP_FOR_CLIENT: '/shop-for-client',
  AXISMATRIX_APPROVALS: '/axis-matrix-approvals',
  AXISMATRIX_RESULTS: '/axis-matrix-results',
} as const;

export type RoleId = (typeof ROLES)[keyof typeof ROLES];

export function isFullAdminRole(roleId?: number | string | null) {
  return Number(roleId) === ROLES.ADMIN;
}

export function isCmsAdminRole(roleId?: number | string | null) {
  return Number(roleId) === ROLES.CMS_ADMIN;
}

export function isAdminRole(roleId?: number | string | null) {
  return isFullAdminRole(roleId) || isCmsAdminRole(roleId);
}

export function getAdminHomeRoute(roleId?: number | string | null) {
  return isCmsAdminRole(roleId) ? ROUTES.ADMIN_CONTENT : ROUTES.ADMIN_DASHBOARD;
}

export const PATIENT_STATUS_LABELS: Record<number, string> = {
  1: 'PENDING',
  2: 'INCOMPLETE',
  3: 'ASSIGNED',
  4: 'ACTIVE',
  5: 'INACTIVE',
};

export const LAB_RESULT_STATUS_LABELS: Record<number, string> = {
  0: 'NEW',
  1: 'REVIEWED',
  2: 'PATTERN ASSIGNED',
  3: 'UPLOADING',
  4: 'UPLOAD FAILED',
  5: 'DIRECTOR REVIEW',
  6: 'DELAY',
};
